import React, { useEffect, useMemo, useState } from 'react';

import { CheckCircleFilled } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Alert, Button, Form, Input, Spin } from 'antd';

import { errorNotification } from 'appRedux/actions';
import IntlMessages from 'util/IntlMessages';
import { get, set } from 'packages/utils/storage';
import { resolveFirebaseAuthErrorMessage } from '@src/packages/utils/auth/firebaseAuthErrorMessage';
import {
  getAuth,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
} from 'firebase/auth';

import AppBrand from 'components/AppBrand';
import Card from '../../components/Card';
import authStyles from '../authShared.module.less';
import styles from './styles.module.less';

/** Align with react-intl locale so Firebase errors match on-screen language. */
const authErrorLocaleFromIntl = intlLocale => {
  if (!intlLocale || typeof intlLocale !== 'string') {
    return 'en';
  }
  return intlLocale.toLowerCase().startsWith('es') ? 'es' : 'en';
};

const EMAIL_MAX_LEN = 254;
const EMAIL_LINK_PATH = '/email_link';

const EmailLink = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const authErrorLocale = useMemo(() => authErrorLocaleFromIntl(intl.locale), [intl.locale]);

  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [staleEmailHint, setStaleEmailHint] = useState(false);

  const busy = submitLoading || magicLinkLoading;

  const spinTip = !busy
    ? ''
    : magicLinkLoading
      ? intl.formatMessage({ id: 'auth.emailLink.completingSignIn' })
      : intl.formatMessage({ id: 'auth.emailLink.sendingLink' });

  const onFinish = async values => {
    setSubmitLoading(true);
    try {
      const auth = getAuth();
      auth.useDeviceLanguage();

      const actionCodeSettings = {
        url: `${window.location.origin}${EMAIL_LINK_PATH}`,
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, values.email, actionCodeSettings);
      set('emailForSignIn', values.email);
      setEmailSentSuccess(true);
      setStaleEmailHint(false);
    } catch (err) {
      dispatch(errorNotification(resolveFirebaseAuthErrorMessage(err, authErrorLocale)));
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const email = get('emailForSignIn');
    if (!email) {
      return undefined;
    }
    const auth = getAuth();
    if (isSignInWithEmailLink(auth, window.location.href)) {
      setMagicLinkLoading(true);
      signInWithEmailLink(auth, email, window.location.href)
        .then(result => {
          if (cancelled) {
            return;
          }
          set('user_id', result.user.uid);

          // force reload to cleanup
          document.location.href = '/';
        })
        .catch(err => {
          if (cancelled) {
            return;
          }
          dispatch(errorNotification(resolveFirebaseAuthErrorMessage(err, authErrorLocale)));
          setMagicLinkLoading(false);
        });
    } else {
      setStaleEmailHint(true);
    }
    return () => {
      cancelled = true;
    };
  }, [dispatch, authErrorLocale]);

  const footer = (
    <div
      className={classnames(
        'gx-flex-column gx-justify-content-center gx-align-items-center gx-w-100',
        authStyles.footer,
      )}
    >
      <div>
        <Link className={authStyles.footerLink} to="/signin">
          <IntlMessages id="auth.enter.another.methods" />
        </Link>
      </div>
    </div>
  );

  const formDescribedBy = staleEmailHint
    ? 'email-link-intro email-link-stale-hint'
    : 'email-link-intro';

  return (
    <Card className={authStyles.card} footer={footer}>
      <Spin spinning={busy} tip={spinTip || undefined} wrapperClassName={styles.spinInCard}>
        {emailSentSuccess ? (
          <main
            className={authStyles.main}
            id="email-link-success-main"
            aria-labelledby="email-link-success-title"
          >
            <div className={styles.successHero}>
              <div className={styles.successIcon} aria-hidden="true">
                <CheckCircleFilled className={styles.successIconGlyph} />
              </div>
              <h1 id="email-link-success-title" className={authStyles.title}>
                <IntlMessages id="auth.emailLink.successTitle" />
              </h1>
              <p className={styles.successBody}>
                <IntlMessages id="auth.emailLink.successBody" />
              </p>
            </div>
          </main>
        ) : (
          <main
            className={authStyles.main}
            id="email-link-main"
            aria-labelledby="app-brand-title"
          >
            <AppBrand authHero className={authStyles.appBrand} titleAs="h1">
              <IntlMessages id="auth.emailLink.title" />
            </AppBrand>
            <p id="email-link-intro" className={styles.intro}>
              <IntlMessages id="auth.emailLink.intro" />
            </p>
            {staleEmailHint && (
              <Alert
                id="email-link-stale-hint"
                className={styles.staleAlert}
                type="info"
                showIcon
                message={intl.formatMessage({ id: 'auth.emailLink.staleSessionHint' })}
              />
            )}
            <Form
              component="form"
              layout="vertical"
              requiredMark
              name="emailLink"
              className={classnames('gx-signin-form gx-form-row0', authStyles.form)}
              onFinish={onFinish}
              aria-describedby={formDescribedBy}
            >
              <Form.Item
                rules={[
                  {
                    type: 'email',
                    message: intl.formatMessage({ id: 'form.format.msg' }),
                  },
                  { required: true, message: intl.formatMessage({ id: 'form.required.msg' }) },
                ]}
                name="email"
                label={intl.formatMessage({ id: 'appModule.email' })}
              >
                <Input
                  autoComplete="email"
                  disabled={busy}
                  maxLength={EMAIL_MAX_LEN}
                  inputMode="email"
                />
              </Form.Item>
              <Form.Item>
                <Button
                  loading={submitLoading}
                  type="primary"
                  className={classnames('gx-mb-0', authStyles.submitButton)}
                  htmlType="submit"
                  block
                  disabled={magicLinkLoading}
                >
                  <IntlMessages id="app.userAuth.send" />
                </Button>
              </Form.Item>
            </Form>
          </main>
        )}
      </Spin>
    </Card>
  );
};

export default EmailLink;
